import { convertToNumberIfDigits } from "../../../../util/pocUtils";
import { FormField } from "../../../../services/restAPI/FormV3Types";
import { hasValue } from "./payloadValidator";

export const NUMERIC_FIELD_TYPES = new Set(["Number", "Decimal"]);

export const prepareValuesToSave = (
   formData: Record<string, any>,
   formFieldObjects: FormField[],
) => {
   const preparedFormData = { ...(formData || {}) };

   Object.keys(preparedFormData).forEach((fieldId) => {
      const fieldDefinition = (formFieldObjects || []).find((field) => field?.id === fieldId);
      if (!fieldDefinition) return;

      if (NUMERIC_FIELD_TYPES.has(fieldDefinition?.type)) {
         const currentValue = preparedFormData[fieldId];

         if (currentValue === "" || currentValue === null || currentValue === undefined) {
            preparedFormData[fieldId] = currentValue;
            return;
         }

         const numberValue = Number(currentValue);
         preparedFormData[fieldId] = Number.isFinite(numberValue) ? numberValue : currentValue;
         return;
      }

      if (fieldDefinition?.type === "File") {
         const currentValue = preparedFormData[fieldId];
         if (
            currentValue &&
            typeof currentValue === "object" &&
            "originalValue" in currentValue &&
            "decoratedValue" in currentValue
         ) {
            preparedFormData[fieldId] = currentValue.originalValue ?? null;
         }
      }
   });

   delete preparedFormData.formId;

   Object.keys(preparedFormData).forEach((key) => {
      const fieldValue = preparedFormData[key];
      if (
         fieldValue &&
         typeof fieldValue === "object" &&
         !Array.isArray(fieldValue) &&
         "value" in fieldValue
      ) {
         preparedFormData[key] = fieldValue.value;
      }
   });

   if (Object.prototype.hasOwnProperty.call(preparedFormData, "678508100a7ae9594db5e682")) {
      preparedFormData["678508100a7ae9594db5e682"] = "507b6ed4-3daf-4a7a-bf80-efc84c705add";
   }
   if (Object.prototype.hasOwnProperty.call(preparedFormData, "678508100a7ae9594db5e683")) {
      preparedFormData["678508100a7ae9594db5e683"] = "Sklep";
   }

   return preparedFormData;
};

type BuildProcessedFormValuesArgs = {
   preparedFormValuesObject: Record<string, any>;
   listTableFieldIds: string[];
   hasCheckboxColumnById: Record<string, boolean>;
   listSelections: Record<string, string[]>;
   validRowIdsByListTableFieldId: Record<string, string[]>;
   formFieldObjects: FormField[];
};

export const buildProcessedFormValues = ({
   preparedFormValuesObject,
   listTableFieldIds,
   hasCheckboxColumnById,
   listSelections,
   validRowIdsByListTableFieldId,
   formFieldObjects,
}: BuildProcessedFormValuesArgs) => {
   const fieldTypeById = new Map((formFieldObjects || []).map((f) => [f.id, f.type]));

   return Object.entries(preparedFormValuesObject || {}).reduce<Record<string, any>>(
      (processedValues, [fieldId, value]) => {
         const fieldType = fieldTypeById.get(fieldId);
         const isNumericField = fieldType !== undefined && NUMERIC_FIELD_TYPES.has(fieldType);

         if (Array.isArray(value) && listTableFieldIds.includes(fieldId)) {
            const hasCheckboxes = !!hasCheckboxColumnById?.[fieldId];

            if (hasCheckboxes) {
               const selectedRowIdsForThisField = Array.isArray(listSelections?.[fieldId])
                  ? listSelections[fieldId].map(String)
                  : [];

               const filteredRows = selectedRowIdsForThisField.length
                  ? value.filter((row) => selectedRowIdsForThisField.includes(String(row.id)))
                  : [];
               processedValues[fieldId] = isNumericField
                  ? convertToNumberIfDigits(filteredRows)
                  : filteredRows;

               return processedValues;
            }

            const validRowIdsForThisField = validRowIdsByListTableFieldId?.[fieldId];
            if (Array.isArray(validRowIdsForThisField)) {
               const filteredRows = value.filter((row) =>
                  validRowIdsForThisField.includes(String(row.id)),
               );
               processedValues[fieldId] = isNumericField
                  ? convertToNumberIfDigits(filteredRows)
                  : filteredRows;
               return processedValues;
            }

            processedValues[fieldId] = isNumericField ? convertToNumberIfDigits(value) : value;
            return processedValues;
         }

         processedValues[fieldId] = isNumericField ? convertToNumberIfDigits(value) : value;
         return processedValues;
      },
      {},
   );
};

export const sanitizePayload = (
   payload: Record<string, any>,
   formFields: FormField[] = [],
): Record<string, any> => {
   const sanitizedPayload: Record<string, any> = {};
   const payloadFieldIds = (formFields || [])
      .filter((field) => field?.id && field?.type !== "Section")
      .map((field) => String(field.id));

   payloadFieldIds.forEach((fieldId) => {
      const fieldValue = payload?.[fieldId];

      if (!hasValue(fieldValue)) {
         sanitizedPayload[fieldId] = null;
         return;
      }

      sanitizedPayload[fieldId] = fieldValue;
   });

   Object.entries(payload || {}).forEach(([fieldId, fieldValue]) => {
      if (fieldId in sanitizedPayload) return;
      sanitizedPayload[fieldId] = hasValue(fieldValue) ? fieldValue : null;
   });

   return sanitizedPayload;
};
